/**
 * The trail ribbon (Sprint 3, S3-12): a screen-space line like three's LineMaterial (one quad per segment, width in
 * px), adapted for a fleet:
 *
 * - **One draw call per spacecraft** (plus its halo): the trail is a single instanced strip. Each instance reads two
 *   consecutive samples from the same buffers (offsets 0 and one sample), so every sample is stored once.
 * - **Float64 precision on the GPU**: positions are uploaded as a float32 high part and a float32 low part
 *   (x = high + low); the shader subtracts the floating origin part by part. A trail is exact everywhere, even zoomed
 *   in at 160 AU, without chunks anchored at nearby samples (Sprint 2 needed ~3 draw calls per 128 samples).
 * - **Brightness from time, in the shader**: each sample carries its mission time, and the comet tail (S2-09), the
 *   orbiter trail memory (D10), the silent dimming and the dashed prediction (D11) and the highlight of the selected
 *   mission (D9) are computed per fragment from uniforms — no per-frame colour uploads.
 * - **Stale part after a scale-mode change** (S4-15 item 3): segments starting at or before `uFreshFrom` are still
 *   mapped with an older profile; they fade with `uStaleAlpha` and are not drawn once it reaches 0.
 */
import * as THREE from 'three';

const vertexShader = /* glsl */ `
#include <common>
#include <logdepthbuf_pars_vertex>

uniform float linewidth;
uniform vec2 resolution;
uniform vec3 uOriginHigh;
uniform vec3 uOriginLow;
uniform float uNow;
uniform vec2 uPhase;
uniform float uMemory;
uniform float uFreshFrom;
uniform float uStaleAlpha;

attribute vec3 instanceStart;
attribute vec3 instanceEnd;
attribute vec3 instanceStartLow;
attribute vec3 instanceEndLow;
attribute float instanceTStart;
attribute float instanceTEnd;

varying vec2 vUv;
varying float vT;
varying float vStale;

float trimSegmentAlpha(const in vec4 start, const in vec4 end) {
  float a = projectionMatrix[2][2];
  float b = projectionMatrix[3][2];
  float nearEstimate = (a > 0.0) ? (-b / (a + 1.0)) : (-0.5 * b / a);
  return (nearEstimate - start.z) / (end.z - start.z);
}

bool forgotten(float t) {
  return t >= uPhase.x && t <= uPhase.y && uNow - t > uMemory;
}

void main() {
  vT = (position.y < 0.5) ? instanceTStart : instanceTEnd;
  // segments beyond the orbiter's trail memory are not drawn at all (their samples may not even be computed)
  if (forgotten(instanceTStart) || forgotten(instanceTEnd)) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  // a whole segment is stale when its start is: its start is where the stale positions are
  vStale = instanceTStart <= uFreshFrom ? 1.0 : 0.0;
  if (vStale > 0.5 && uStaleAlpha <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  float aspect = resolution.x / resolution.y;
  // relative to the floating origin, high and low parts separately (emulated float64)
  vec3 s = (instanceStart - uOriginHigh) + (instanceStartLow - uOriginLow);
  vec3 e = (instanceEnd - uOriginHigh) + (instanceEndLow - uOriginLow);
  vec4 start = modelViewMatrix * vec4(s, 1.0);
  vec4 end = modelViewMatrix * vec4(e, 1.0);
  vUv = uv;

  bool perspective = (projectionMatrix[2][3] == -1.0);
  if (perspective) {
    if (start.z < 0.0 && end.z >= 0.0) {
      end.xyz = mix(start.xyz, end.xyz, trimSegmentAlpha(start, end));
    } else if (end.z < 0.0 && start.z >= 0.0) {
      start.xyz = mix(end.xyz, start.xyz, trimSegmentAlpha(end, start));
    }
  }
  vec4 clipStart = projectionMatrix * start;
  vec4 clipEnd = projectionMatrix * end;
  vec3 ndcStart = clipStart.xyz / clipStart.w;
  vec3 ndcEnd = clipEnd.xyz / clipEnd.w;
  vec2 dir = ndcEnd.xy - ndcStart.xy;
  dir.x *= aspect;
  dir = normalize(dir);
  vec2 offset = vec2(dir.y, -dir.x);
  dir.x /= aspect;
  offset.x /= aspect;
  if (position.x < 0.0) offset *= -1.0;
  if (position.y < 0.0) {
    offset += -dir;
  } else if (position.y > 1.0) {
    offset += dir;
  }
  offset *= linewidth;
  offset /= resolution.y;
  vec4 clip = (position.y < 0.5) ? clipStart : clipEnd;
  offset *= clip.w;
  clip.xy += offset;
  gl_Position = clip;
  vec4 mvPosition = (position.y < 0.5) ? start : end;
  #include <logdepthbuf_vertex>
}
`;

const fragmentShader = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
uniform float uNow;
uniform float uTail;
uniform float uBase;
uniform float uHead;
uniform float uHighlight;
uniform vec2 uPhase;
uniform float uMemory;
uniform float uSilentFrom;
uniform float uSilentGain;
uniform float uPredictedFrom;
uniform float uDash;
uniform float uDotted;
uniform float uDot;
uniform float uStaleAlpha;

varying vec2 vUv;
varying float vT;
varying float vStale;

#include <common>
#include <logdepthbuf_pars_fragment>

void main() {
  #include <logdepthbuf_fragment>
  // round end caps (as LineMaterial)
  if (abs(vUv.y) > 1.0) {
    float a = vUv.x;
    float b = (vUv.y > 0.0) ? vUv.y - 1.0 : vUv.y + 1.0;
    if (a * a + b * b > 1.0) discard;
  }
  // predicted part (after the real "now"): dashed in mission time
  if (vT > uPredictedFrom && mod(vT - uPredictedFrom, uDash) > 0.5 * uDash) discard;
  // reconstructed trajectory (S4-09, D8): dotted — short dots, also in mission time, so they stay put while zooming
  if (uDotted > 0.5 && mod(vT, uDot) > 0.35 * uDot) discard;
  float age = max(uNow - vT, 0.0);
  // comet tail: bright at the head, decaying to a dim base
  float k = uBase + (uHead - uBase) * exp(-age / uTail);
  // silent (contact lost): dimmed
  if (vT > uSilentFrom) k *= uSilentGain;
  // orbiter trail memory: fades out over the last part of the memory span
  float alpha = uOpacity;
  if (vT >= uPhase.x && vT <= uPhase.y) alpha *= 1.0 - smoothstep(0.5 * uMemory, uMemory, age);
  alpha *= mix(1.0, uStaleAlpha, vStale);
  if (alpha <= 0.0) discard;
  gl_FragColor = vec4(uColor * k * uHighlight, alpha);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface RibbonUniforms {
  [name: string]: THREE.IUniform;
  linewidth: THREE.IUniform<number>;
  resolution: THREE.IUniform<THREE.Vector2>;
  uOriginHigh: THREE.IUniform<THREE.Vector3>;
  uOriginLow: THREE.IUniform<THREE.Vector3>;
  uColor: THREE.IUniform<THREE.Color>;
  uOpacity: THREE.IUniform<number>;
  uNow: THREE.IUniform<number>;
  uTail: THREE.IUniform<number>;
  uBase: THREE.IUniform<number>;
  uHead: THREE.IUniform<number>;
  uHighlight: THREE.IUniform<number>;
  uPhase: THREE.IUniform<THREE.Vector2>;
  uMemory: THREE.IUniform<number>;
  uSilentFrom: THREE.IUniform<number>;
  uSilentGain: THREE.IUniform<number>;
  uPredictedFrom: THREE.IUniform<number>;
  uDash: THREE.IUniform<number>;
  /** 1: the whole trail is dotted (a reconstructed trajectory), 0: solid. */
  uDotted: THREE.IUniform<number>;
  /** Dot period, days of mission time. */
  uDot: THREE.IUniform<number>;
  /** Segments starting at or before this mission time are mapped with an older profile (−NEVER: none). */
  uFreshFrom: THREE.IUniform<number>;
  /** Their opacity: 1 when they became stale, fading to 0. */
  uStaleAlpha: THREE.IUniform<number>;
}

/** A large time for "never" in float32 uniforms (mission days relative to the line's epoch). */
export const NEVER = 1e9;

export function createRibbonUniforms(color: THREE.Color): RibbonUniforms {
  return {
    linewidth: { value: 2.5 },
    resolution: { value: new THREE.Vector2(1, 1) },
    uOriginHigh: { value: new THREE.Vector3() },
    uOriginLow: { value: new THREE.Vector3() },
    uColor: { value: color },
    uOpacity: { value: 1 },
    uNow: { value: 0 },
    uTail: { value: 60 },
    uBase: { value: 0.3 },
    uHead: { value: 1 },
    uHighlight: { value: 1 },
    uPhase: { value: new THREE.Vector2(NEVER, -NEVER) },
    uMemory: { value: 90 },
    uSilentFrom: { value: NEVER },
    uSilentGain: { value: 0.5 },
    uPredictedFrom: { value: NEVER },
    uDash: { value: 20 },
    uDotted: { value: 0 },
    uDot: { value: 0.02 },
    uFreshFrom: { value: -NEVER },
    uStaleAlpha: { value: 0 },
  };
}

/**
 * The ribbon material; `shared` uniforms are referenced, not copied, so the halo (same geometry, wider, translucent)
 * follows the ribbon's time and origin without extra work.
 */
export function createRibbonMaterial(
  uniforms: RibbonUniforms,
  overrides: Partial<Pick<RibbonUniforms, 'linewidth' | 'uOpacity'>> = {},
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    name: 'RibbonMaterial',
    uniforms: { ...uniforms, ...overrides },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
  });
}

/** The quad of one segment (as three's LineSegmentsGeometry): 8 vertices, 6 triangles incl. the end caps. */
export function createRibbonGeometry(): THREE.InstancedBufferGeometry {
  const g = new THREE.InstancedBufferGeometry();
  g.setIndex([0, 2, 1, 2, 3, 1, 2, 4, 3, 4, 5, 3, 4, 6, 5, 6, 7, 5]);
  g.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([-1, 2, 0, 1, 2, 0, -1, 1, 0, 1, 1, 0, -1, 0, 0, 1, 0, 0, -1, -1, 0, 1, -1, 0], 3),
  );
  g.setAttribute('uv', new THREE.Float32BufferAttribute([-1, 2, 1, 2, -1, 1, 1, 1, -1, -1, 1, -1, -1, -2, 1, -2], 2));
  return g;
}

/** Splits a float64 value into float32 high and low parts: x ≈ high + low to ~float64 precision. */
export function splitDouble(x: number): [number, number] {
  const high = Math.fround(x);
  return [high, Math.fround(x - high)];
}
